import { useQuery } from '@apollo/client';

import mergeConnectionResults from 'common/graphql/mergeConnectionResults';
import useCompanyContext from 'common/hooks/useCompanyContext';

import {
  BankTransferRecipientsDocument,
  BankTransferRecipientsQuery,
} from './bankTransferRecipients.gql';
import mapQueryResultToBeneficiarySectionList from './mapQueryResultToBeneficiarySectionList';

const RECIPIENTS_PER_PAGE = 1000;

const bankTransferRecipientEquals = (
  a: BankTransferRecipientsQuery['viewer']['company']['bankTransferRecipients']['edges'][number],
  b: BankTransferRecipientsQuery['viewer']['company']['bankTransferRecipients']['edges'][number],
) => a.node.bankTransferRecipientId === b.node.bankTransferRecipientId;

const mergeTransferRecipientConnectionResults = mergeConnectionResults(
  bankTransferRecipientEquals,
);

const useBeneficiaries = () => {
  const { companyProfileId } = useCompanyContext();

  if (!companyProfileId) {
    throw new Error('companyProfileId is undefined in useBeneficiaries');
  }

  const { data, error, fetchMore, loading } = useQuery(
    BankTransferRecipientsDocument,
    {
      fetchPolicy: 'cache-and-network',
      variables: {
        after: null,
        companyProfileId,
        first: RECIPIENTS_PER_PAGE,
      },
    },
  );

  const hasMoreToFetch = Boolean(
    data?.viewer.company.bankTransferRecipients.pageInfo.hasNextPage,
  );

  const beneficiaries = data
    ? mapQueryResultToBeneficiarySectionList(data)
    : [];

  const loadMore = () => {
    fetchMore({
      updateQuery: (previousResult, { fetchMoreResult }) => {
        const isEqual =
          previousResult.viewer.company.bankTransferRecipients.pageInfo
            .endCursor ===
          fetchMoreResult.viewer.company.bankTransferRecipients.pageInfo
            .endCursor;
        if (isEqual) {
          return previousResult;
        }
        return mergeTransferRecipientConnectionResults(
          previousResult,
          fetchMoreResult,
        );
      },
      variables: {
        after: data?.viewer.company.bankTransferRecipients.pageInfo.endCursor,
      },
    });
  };

  return {
    beneficiaries,
    error,
    hasMoreToFetch,
    isLoading: loading,
    loadMore,
  };
};

export default useBeneficiaries;
